import { environment } from 'environments/environment';

/**
 * Turns a storage-account file descriptor's `url` into one the browser can
 * actually follow from an `href` / `src`.
 *
 * A PRIVATE file is persisted with a ROOT-RELATIVE reference
 * (`/api/storage-account/file-manager/private/<segments>/<filename>`) and signed
 * per read, so the url that reaches the client carries `exp`/`sig` but still no
 * origin. The SPA and the API are separate origins — `environment.api.rest`
 * points at the API host and there is no dev proxy back to the SPA — so handing
 * that url straight to `[href]` makes the browser resolve it against the SPA
 * origin and 404.
 *
 * Only the ORIGIN is prepended, never the full `rest` path: the persisted url
 * already starts with `/api`, and prepending the whole base would double it.
 *
 * An ABSOLUTE url (an Azure blob with its SAS query, for instance) already
 * carries its own origin and passes through untouched.
 *
 * Assumes the two shapes the storage providers actually emit: single-slash
 * root-relative, or fully absolute. A protocol-relative `//host/path` would be
 * mangled — add a guard here first if this ever serves a source that emits one.
 *
 * @param url the descriptor's `url`, as it arrives on the `file` JSON scalar
 * @returns a followable absolute url, or `null` when there is no url at all
 */
export function resolveStorageFileUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  if (!url.startsWith('/')) {
    return url;
  }

  return new URL(environment.api.rest).origin + url;
}
